import {
  createDurationHistogram,
  type DurationHistogram,
  histogramBucket,
} from './effect-histogram.ts'
import type { SegmenterPlatform } from './segmenter-catalog.ts'
import { median } from './stats.ts'

export interface MeasureBudget {
  costMs: number
  discardMs: number
  windowMs: number
  windows: number
  deliveredShare: number
  requestedShare: number
  gpuBehindShare: number
  minCostSamples: number
  minGpuFrames: number
  iouMin: number
  maskSamples: number
  sampleDeadlineMs: number
}

const SHARED_BUDGET = {
  discardMs: 5000,
  windowMs: 5000,
  windows: 2,
  deliveredShare: 0.9,
  requestedShare: 0.5,
  gpuBehindShare: 0.5,
  minCostSamples: 30,
  minGpuFrames: 10,
  iouMin: 0.8,
  maskSamples: 3,
  sampleDeadlineMs: 10_000,
} as const

export const PHONE_MEASURE: MeasureBudget = { ...SHARED_BUDGET, costMs: 20 }

export const DESKTOP_MEASURE: MeasureBudget = { ...SHARED_BUDGET, costMs: 12 }

export function measureBudget(platform: SegmenterPlatform): MeasureBudget {
  return platform === 'phone' ? PHONE_MEASURE : DESKTOP_MEASURE
}

export type WindowReason = 'delivered' | 'cost' | 'gpu'

export const ALL_WINDOW_REASONS: readonly WindowReason[] = ['delivered', 'cost']

export interface WindowInput {
  deliveredFrames: number
  cameraFrames: number
  requestedFrames?: number
  gpuBehindFrames: number
  cost: Pick<DurationHistogram, 'count' | 'percentile'>
  segmentationInterval?: number
}

export interface WindowJudgement {
  bad: boolean
  reasons: WindowReason[]
}

export function minCostSamples(budget: MeasureBudget, segmentationInterval = 1): number {
  const every = Number.isFinite(segmentationInterval)
    ? Math.max(1, Math.floor(segmentationInterval))
    : 1
  return Math.ceil(budget.minCostSamples / every)
}

export function judgeWindow(input: WindowInput, budget: MeasureBudget): WindowJudgement {
  const reasons: WindowReason[] = []

  const requested = input.requestedFrames ?? 0
  const delivered =
    input.cameraFrames > 0
      ? Math.min(input.deliveredFrames, input.cameraFrames)
      : input.deliveredFrames
  const behind =
    input.cameraFrames > 0
      ? delivered < input.cameraFrames * budget.deliveredShare
      : requested > 0 && delivered < requested * budget.requestedShare
  if (behind) reasons.push('delivered')

  if (input.cost.count >= minCostSamples(budget, input.segmentationInterval)) {
    const median = input.cost.percentile(50)
    if (median !== null && median > budget.costMs) reasons.push('cost')
  }

  if (
    input.deliveredFrames >= budget.minGpuFrames &&
    input.gpuBehindFrames / input.deliveredFrames > budget.gpuBehindShare
  ) {
    reasons.push('gpu')
  }

  // A fence also reads unsignalled on a backlog after a stalled task: alone it floored a healthy call.
  return { bad: reasons.some((reason) => reason !== 'gpu'), reasons }
}

export type MeasureVerdict =
  | 'passed'
  | 'slow'
  | 'wrong_output'
  | 'context_lost'
  | 'timeout'
  | 'cancelled'

export interface MeasureResult {
  verdict: MeasureVerdict
  proven: boolean
  reasons: WindowReason[]
}

export type MeasureStatus = 'discarding' | 'timing' | 'sampling' | MeasureResult

export interface MeasureFrame {
  at: number
  costMs: number
  segmented: boolean
  gpuBehind: boolean
}

export interface MeasureSummary {
  costP50Ms: number | null
  costP90Ms: number | null
  gpuBehindShare: number | null
  deliveredFps: number | null
  cameraFps: number | null
  iouMin: number | null
  iouMedian: number | null
  conclusiveSamples: number
  windowsJudged: number
  durationMs: number
}

export interface EffectMeasurement {
  recordFrame(frame: MeasureFrame): void
  recordCameraFrames(count: number, at: number): void
  recordRequestedFrames(count: number, at: number): void
  recordMaskSample(iou: number | null): void
  contextLost(at: number): void
  cancel(at: number): void
  status(now: number): MeasureStatus
  summary(): MeasureSummary
}

export interface EffectMeasurementOptions {
  segmentationInterval?: number
}

export function createEffectMeasurement(
  budget: MeasureBudget,
  startedAt: number,
  { segmentationInterval = 1 }: EffectMeasurementOptions = {},
): EffectMeasurement {
  const timingStart = startedAt + budget.discardMs
  const timingEnd = timingStart + budget.windows * budget.windowMs
  const deadline = timingEnd + budget.sampleDeadlineMs

  const windowCost = createDurationHistogram()
  const totalCost = createDurationHistogram()
  let windowIndex = 0
  let delivered = 0
  let camera = 0
  let requested = 0
  let gpuBehind = 0
  let totalDelivered = 0
  let totalCamera = 0
  let totalGpuBehind = 0
  let judged = 0
  let consecutiveBad = 0
  let badReasons: WindowReason[] = []
  const ious: number[] = []
  let samples = 0
  let result: MeasureResult | null = null
  let lastSeen = startedAt
  let finishedAt: number | null = null

  const conclusive = () => ious.length > 0

  function finish(verdict: MeasureVerdict, at: number, reasons: WindowReason[] = []) {
    if (result) return
    result = { verdict, proven: verdict === 'passed' && conclusive(), reasons }
    finishedAt = at
  }

  function closeWindow(at: number) {
    const judgement = judgeWindow(
      {
        deliveredFrames: delivered,
        cameraFrames: camera,
        requestedFrames: requested,
        gpuBehindFrames: gpuBehind,
        cost: windowCost,
        segmentationInterval,
      },
      budget,
    )
    judged += 1
    totalDelivered += delivered
    totalCamera += camera
    totalGpuBehind += gpuBehind
    consecutiveBad = judgement.bad ? consecutiveBad + 1 : 0
    badReasons = judgement.bad ? [...new Set([...badReasons, ...judgement.reasons])] : []
    delivered = 0
    camera = 0
    requested = 0
    gpuBehind = 0
    windowCost.reset()
    windowIndex += 1
    if (consecutiveBad >= budget.windows) finish('slow', at, badReasons)
  }

  function advance(now: number) {
    if (now > lastSeen) lastSeen = now
    while (!result && windowIndex < budget.windows) {
      const windowEnd = timingStart + (windowIndex + 1) * budget.windowMs
      if (now < windowEnd) break
      closeWindow(windowEnd)
    }
    if (!result && now >= deadline && windowIndex >= budget.windows) finish('timeout', deadline)
  }

  function windowOf(at: number): number {
    return Math.floor((at - timingStart) / budget.windowMs)
  }

  function accepts(at: number): boolean {
    advance(at)
    return !result && at >= timingStart && windowOf(at) === windowIndex
  }

  return {
    recordFrame(frame) {
      if (!accepts(frame.at)) return
      delivered += 1
      if (frame.gpuBehind) gpuBehind += 1
      if (frame.segmented) {
        const bucket = histogramBucket(frame.costMs)
        windowCost.add(frame.costMs, bucket)
        totalCost.add(frame.costMs, bucket)
      }
    },
    recordCameraFrames(count, at) {
      if (!accepts(at) || !Number.isFinite(count) || count <= 0) return
      camera += count
    },
    recordRequestedFrames(count, at) {
      if (!accepts(at) || !Number.isFinite(count) || count <= 0) return
      requested += count
    },
    recordMaskSample(iou) {
      if (result || windowIndex < budget.windows || samples >= budget.maskSamples) return
      samples += 1
      if (iou !== null && Number.isFinite(iou)) ious.push(iou)
      if (samples < budget.maskSamples) return
      const agreement = median(ious)
      finish(agreement !== null && agreement < budget.iouMin ? 'wrong_output' : 'passed', lastSeen)
    },
    contextLost(at) {
      advance(at)
      finish('context_lost', at)
    },
    cancel(at) {
      advance(at)
      finish('cancelled', at)
    },
    status(now) {
      advance(now)
      if (result) return result
      if (now < timingStart) return 'discarding'
      return windowIndex < budget.windows ? 'timing' : 'sampling'
    },
    summary() {
      const seconds = (judged * budget.windowMs) / 1000
      const hasWindows = judged * budget.windowMs > 0
      return {
        costP50Ms: totalCost.percentile(50),
        costP90Ms: totalCost.percentile(90),
        gpuBehindShare: totalDelivered > 0 ? totalGpuBehind / totalDelivered : null,
        deliveredFps: hasWindows ? totalDelivered / seconds : null,
        cameraFps: hasWindows && totalCamera > 0 ? totalCamera / seconds : null,
        iouMin: ious.length > 0 ? Math.min(...ious) : null,
        iouMedian: median(ious),
        conclusiveSamples: ious.length,
        windowsJudged: judged,
        durationMs: (finishedAt ?? lastSeen) - startedAt,
      }
    },
  }
}
